"use client";

import { resetDatadogSession } from "@/app/datadog-init";
import { setUserLocale } from "@/i18n/locales";
import {
  getBuildVersion,
  initializeVersionTracking,
  shouldRefreshForUpdate,
} from "@/lib/app-version";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { cancelPOSPaymentByReaderId } from "@frontend/lib/actions/stripe-actions";
import type { LanguageCode } from "@frontend/lib/graphql/generated-gql";
import { useKioskHeartbeat } from "@frontend/lib/hooks/useKioskHeartbeat";
import { useStripeReaderContext } from "@frontend/lib/providers/StripeReaderProvider";
import { cn } from "@frontend/shared/lib/utils";
import { useSelector } from "@xstate/react";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import type { StateNode } from "xstate";
import { MedriteLoader } from "../components/MedriteLoader";
import { KioskDevTool } from "../devtool/KioskDevTool";
import {
  generatePlaywrightTest,
  logTraceCompletionToDataDog,
} from "../devtool/playwright-generator";
import { traceInspector } from "../devtool/trace-inspector";
import { domEventSpy, useDOMEventSpy } from "../lib/dom-event-spy";
import { getKioskIdentity } from "../lib/kiosk-identity";
import { preloadComponents } from "../lib/lazy-component-loader";
import { clearAllStates } from "../lib/state-storage";
import { serializeStateHybrid } from "../machine/state-serializer";
import { clearKioskPatientSession } from "../server-actions/otp-actions";
import type { KioskComponentProps } from "../types";
import { useKiosk } from "./kiosk-provider";
import { ComponentErrorBoundary } from "./kiosk_components/ComponentErrorBoundary";
import { InactivityTimeout } from "./kiosk_components/InactivityTimeout";
import { KioskHeader } from "./kiosk_components/KioskHeader";
import { RestartDialog } from "./kiosk_components/RestartDialog";

import { getContextSensitiveOverrides } from "../lib/context-sensitive-content";
import {
  readEffectiveKioskSettings,
  readKioskNavigationMode,
} from "../lib/kiosk-settings";
import { identityArc } from "../machine/arcs/identity.arc";
import { insuranceArc } from "../machine/arcs/insurance.arc";
import { intakeArc } from "../machine/arcs/intake.arc";
import { returningPatientArc } from "../machine/arcs/returning-patient.arc";
// Arc machines (for component hydration — child states aren't in parent machine.states)
import { triageArc } from "../machine/arcs/triage.arc";
import {
  buildArcStateToActorMap,
  dotToStateValue,
} from "../machine/helper/arc-helpers";

const ARC_MACHINES = [
  triageArc,
  returningPatientArc,
  identityArc,
  insuranceArc,
  intakeArc,
];

// Configure navigation mode - can be controlled from DevTool Settings.
// Defaults sourced from lib/kiosk-settings so the fleet heartbeat and the
// runtime can't drift.
const NAVIGATION_MODE = readKioskNavigationMode();

export function KioskRenderer() {
  const {
    bundle,
    isLoading,
    error,
    machine,
    state,
    send,
    resetMachine,
    configHealthCheck,
    rerunHealthCheck,
  } = useKiosk();

  // Start DOM event spy for capturing real user interactions
  useDOMEventSpy(true);

  // Component registry (lazy loaded)
  const [componentRegistry, setComponentRegistry] = useState<
    Map<string, React.ComponentType<KioskComponentProps>>
  >(new Map());
  const [isHydrating, setIsHydrating] = useState(true);
  const [inactivityTimeout, setInactivityTimeout] = useState(60000); // 1 minute default

  // DevTool open state (lifted so RestartDialog can trigger it)
  const [devToolOpen, setDevToolOpen] = useState(false);

  // Track if we should persist state (false during restart/timeout)
  const shouldPersistState = useRef(true);

  // Stripe Reader ID from machine context (used for DevTool, localStorage, restart URL)
  const stripeReaderId =
    (state.context.stripeReaderId as string | null | undefined) || null;

  // Full reader state from StripeReaderProvider — has label + location + live online status.
  // Only populated when terminal was connected via activation code / PIN flow.
  const { stripeReader, isOnline } = useStripeReaderContext();
  const stripeLocationId =
    stripeReader?.location != null
      ? typeof stripeReader.location === "string"
        ? stripeReader.location
        : stripeReader.location.id
      : null;

  // Fall back to machine context reader ID when StripeReaderProvider hasn't loaded
  // the reader (e.g. terminal configured via ?terminalId= URL param or DevTool).
  const effectiveTerminalId = stripeReader?.id ?? stripeReaderId;
  const effectiveTerminalConnected = stripeReader
    ? isOnline
    : Boolean(stripeReaderId);

  // Snapshot of the kiosk's effective device settings — resolves storage +
  // URL params + compiled-in defaults so the heartbeat reflects what the
  // kiosk is actually configured to do (not just what's been explicitly set).
  // Included in the hex-monitor direct POST only (not Datadog logs).
  // Read once on mount — settings only change via DevTool which triggers a reload.
  const kioskSettings = useMemo(readEffectiveKioskSettings, []);

  // Emit periodic heartbeats to Datadog so hex-monitor fleet dashboard can
  // track this kiosk. Only fires on provisioned devices (?heartbeat=1 launch URL).
  useKioskHeartbeat({
    kioskId: getKioskIdentity().kioskId,
    orgId: (state.context.orgId as number | null | undefined) ?? null,
    kioskDisplayState: state.matches("standby") ? "standby" : "active",
    kioskSettings,
    stripeTerminalConnected: effectiveTerminalConnected,
    stripeTerminalId: effectiveTerminalId,
    stripeTerminalLabel: stripeReader?.label ?? null,
    stripeTerminalLocationId: stripeLocationId,
    stripeTerminalActionStatus: stripeReader?.action?.status ?? null,
  });

  // Persist stripeReaderId to localStorage when it changes
  useEffect(() => {
    if (typeof window !== "undefined" && stripeReaderId) {
      localStorage.setItem("kiosk_stripe_reader_id", stripeReaderId);
    }
  }, [stripeReaderId]);

  // Initialize version tracking, kiosk identity, and Datadog context on mount
  useEffect(() => {
    initializeVersionTracking("[kiosk]", "kiosk_version");
    const identity = getKioskIdentity();
    const version = getBuildVersion();

    // Build device info for observability
    const deviceInfo = {
      kioskId: identity.kioskId,
      kioskName: identity.kioskName || "Medrite Kiosk",
      kioskLocation: identity.kioskLocation,
      kioskVersion: version,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenWidth: screen.width,
      screenHeight: screen.height,
      touchPoints: navigator.maxTouchPoints,
    };

    // Set Datadog global context - all future logs/RUM events include this
    datadogLogs.setGlobalContext({ kiosk: deviceInfo });
    datadogRum.setGlobalContext({ kiosk: deviceInfo });

    // Log session start
    datadogLogs.logger.info("[kiosk] Session started", deviceInfo);
  }, []);

  // Load inactivity timeout from localStorage (WKWebView reliable)
  useEffect(() => {
    const loadInactivityTimeout = async () => {
      // Try localStorage first (more reliable on WKWebView)
      try {
        const localStorageTimeout = localStorage.getItem(
          "kiosk_inactivity_timeout",
        );
        if (localStorageTimeout !== null) {
          const timeout = Number.parseInt(localStorageTimeout, 10);
          if (!Number.isNaN(timeout) && timeout > 0) {
            setInactivityTimeout(timeout);

            return;
          }
        }
      } catch (error) {
        console.error("Failed to load timeout from localStorage:", error);
      }
    };

    loadInactivityTimeout();
  }, []);

  const currentStateName = (() => {
    if (typeof state.value === "string") {
      return state.value;
    }

    if (typeof state.value === "object" && state.value !== null) {
      // Nested state: { posPayment: "creatingPaymentIntent" }
      const [parentName] = Object.entries(state.value)[0] ?? [];
      return parentName; // Use parent name we already extracted
    }

    return String(state.value);
  })();

  // ─── Arc child actor subscription ───────────────────────────
  const arcStateToActorMap = useMemo(
    () => buildArcStateToActorMap(machine),
    [machine],
  );
  const childActorId = arcStateToActorMap[currentStateName];
  const childActorRef = childActorId
    ? // biome-ignore lint/suspicious/noExplicitAny: XState children map is not typed for named child actors
      (state.children as any)?.[childActorId]
    : undefined;

  // Subscribe to child actor snapshot — re-renders when child state changes
  const childSnapshot = useSelector(
    childActorRef,
    // biome-ignore lint/suspicious/noExplicitAny: XState child snapshot types are complex
    (snapshot: any) => snapshot,
  );

  // Derive a state key for animations — must change on every state transition
  const stateKey = childSnapshot
    ? JSON.stringify(childSnapshot.value)
    : currentStateName;

  // Poll for version updates while on standby screen
  // biome-ignore lint/correctness/useExhaustiveDependencies: context values are stable machine init params
  useEffect(() => {
    if (currentStateName !== "standby") return;

    const checkForUpdates = async () => {
      try {
        const needsUpdate = await shouldRefreshForUpdate("[kiosk]");

        console.log(
          `[kiosk] Standby update check: needsUpdate=${needsUpdate}`,
          {
            currentVersion: getBuildVersion(),
          },
        );
        if (needsUpdate) {
          datadogLogs.logger.info(
            "[kiosk] Standby detected update, refreshing",
            { currentVersion: getBuildVersion() },
          );
          // Navigate to clean URL with machine initialization params preserved
          // (no stateId = fresh machine, but practiceId/qrMode/etc persist)
          const refreshUrl = new URL(
            window.location.pathname,
            window.location.origin,
          );
          if (state.context.practiceId) {
            refreshUrl.searchParams.set(
              "practiceId",
              state.context.practiceId as string,
            );
          }
          if (state.context.qrMode) {
            refreshUrl.searchParams.set("qrMode", "true");
          }
          if (state.context.ehrLocationId) {
            refreshUrl.searchParams.set(
              "ehrLocationId",
              state.context.ehrLocationId as string,
            );
          }
          if (state.context.stripeReaderId) {
            refreshUrl.searchParams.set(
              "terminalId",
              state.context.stripeReaderId as string,
            );
          }
          const identity = getKioskIdentity();
          if (identity.kioskName) {
            refreshUrl.searchParams.set("kioskName", identity.kioskName);
          }
          window.location.replace(refreshUrl.toString());
        }
      } catch (error) {
        datadogLogs.logger.warn("[kiosk] Standby update check failed", {
          error,
        });
      }
    };

    // Check immediately on entering standby, then poll every 60s
    checkForUpdates();
    const interval = setInterval(checkForUpdates, 60_000);

    return () => clearInterval(interval);
  }, [currentStateName]);

  // Generate random gradient position based on state name (deterministic but appears random)
  const gradientPosition = useMemo(() => {
    const hash = currentStateName.split("").reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    const x = Math.abs(hash % 80) + 10; // 10-90%
    const y = Math.abs((hash >> 8) % 80) + 10; // 10-90%
    return { x, y };
  }, [currentStateName]);

  // Convert machine states to flow format for DevTool (must be before early returns)
  const mockFlow = useMemo(() => {
    // Recursively flatten substates for DevTool visualization
    const flattenStates = (
      stateNode: StateNode,
      parentKey = "",
    ): Record<
      string,
      {
        componentName: string | null;
        contentKey: string | null;
        type: "final" | "normal";
        transitions: never[];
      }
    > => {
      const result: Record<
        string,
        {
          componentName: string | null;
          contentKey: string | null;
          type: "final" | "normal";
          transitions: never[];
        }
      > = {};

      // Add current state (always include for JumpTab, even without meta)
      if (parentKey) {
        result[parentKey] = {
          componentName: stateNode.meta?.componentName || null,
          contentKey: stateNode.meta?.contentKey || null,
          type:
            stateNode.type === "final"
              ? ("final" as const)
              : ("normal" as const),
          transitions: [],
        };
      }

      // Recursively add substates with dot notation (e.g., "insuranceInput.frontPhoto")
      if (stateNode.states && typeof stateNode.states === "object") {
        for (const [childName, childNode] of Object.entries(stateNode.states)) {
          const childKey = parentKey ? `${parentKey}.${childName}` : childName;
          Object.assign(
            result,
            flattenStates(childNode as StateNode, childKey),
          );
        }
      }

      return result;
    };

    const allStates: Record<
      string,
      {
        componentName: string | null;
        contentKey: string | null;
        type: "final" | "normal";
        transitions: never[];
      }
    > = {};

    // Flatten all top-level states
    for (const [stateName, stateNode] of Object.entries(machine.states)) {
      Object.assign(
        allStates,
        flattenStates(stateNode as StateNode, stateName),
      );
    }

    return {
      flowId: "medrite-checkin",
      version: "1.0.0",
      schemaVersion: "v1" as const,
      initialState: "welcome",
      states: allStates,
    };
  }, [machine]);

  // Hydration: Lazy load components based on machine states
  useEffect(() => {
    const hydrateComponents = async () => {
      // Recursively extract component names from all states (including substates)
      const extractComponentNames = (stateNode: StateNode): string[] => {
        const names: string[] = [];

        // Add current state's component name if it exists
        if (stateNode.meta?.componentName) {
          names.push(stateNode.meta.componentName);
        }

        // Recursively extract from substates
        if (stateNode.states && typeof stateNode.states === "object") {
          for (const childStateNode of Object.values(stateNode.states)) {
            names.push(...extractComponentNames(childStateNode as StateNode));
          }
        }

        return names;
      };

      // Extract from all top-level states
      const allComponentNames: string[] = [];
      for (const stateNode of Object.values(machine.states || {})) {
        allComponentNames.push(
          ...extractComponentNames(stateNode as StateNode),
        );
      }

      // Extract from arc machines (their states live outside the parent)
      for (const arcMachine of ARC_MACHINES) {
        for (const stateNode of Object.values(arcMachine.states || {})) {
          allComponentNames.push(
            ...extractComponentNames(stateNode as StateNode),
          );
        }
      }

      // Get unique component names
      const componentNames = Array.from(new Set(allComponentNames));

      // Lazy load all components
      const loadedComponents = await preloadComponents(componentNames);
      setComponentRegistry(loadedComponents);
      setIsHydrating(false);
    };

    hydrateComponents();
  }, [machine]);

  // Log trace to DataDog when entering completion states
  // These states indicate the end of a check-in flow
  const completionStates = [
    "completeDefault",
    "completePayment",
    "completeFrontDesk",
    "completeWorkersCompEPSOrTravel",
    "completeCardOnFile",
    "completePayLater",
  ];

  useEffect(() => {
    if (completionStates.includes(currentStateName)) {
      try {
        const trace = traceInspector.getTrace();
        if (!trace || trace.events.length === 0) {
          console.log("[kiosk-renderer] No trace to log on completion");
          return;
        }

        // Generate the Playwright test
        const generatedTest = generatePlaywrightTest(trace);

        // Log to DataDog
        logTraceCompletionToDataDog(generatedTest, currentStateName);

        console.log(
          `[kiosk-renderer] Logged trace ${trace.traceId} to DataDog on ${currentStateName}`,
        );
      } catch (error) {
        console.warn("[kiosk-renderer] Failed to log trace:", error);
      }
    }
  }, [currentStateName]);

  // Clear stale DOM events when state changes
  // This prevents events from a previous state (e.g., input-otp trailing events)
  // from leaking into the next state's trace
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    domEventSpy.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally trigger on state change
  }, [currentStateName]);

  // Persist state in URL on every state change (except during restart/timeout)
  // This ensures the URL always reflects current state for refresh/reload
  useEffect(() => {
    // Check if we're in a restart flow (flag survives reload via sessionStorage)
    if (
      typeof window !== "undefined" &&
      sessionStorage.getItem("kiosk_restarting") === "true"
    ) {
      sessionStorage.removeItem("kiosk_restarting");
      return;
    }

    if (!shouldPersistState.current) {
      return;
    }

    // Skip persistence if in an arc state but child actor isn't ready yet
    if (childActorId && !childSnapshot) {
      return;
    }

    // Get current stateId from URL to reuse it
    const url = new URL(window.location.href);
    const currentStateId = url.searchParams.get("stateId");

    const serializedData: {
      value: string;
      context: Record<string, unknown>;
      arc?: {
        arcId: string;
        stateName: string | Record<string, unknown>;
        context: Record<string, unknown>;
      };
    } = {
      value: currentStateName,
      context: state.context,
    };

    // Include child arc state for proper restoration on page refresh
    if (childActorId && childSnapshot) {
      serializedData.arc = {
        arcId: childActorId,
        stateName: childSnapshot.value,
        context: childSnapshot.context,
      };
    }

    const { encoded, method } = serializeStateHybrid(
      serializedData,
      currentStateId || undefined,
    );

    // Store in URL for page refreshes
    const paramName = method === "storage" ? "stateId" : "state";
    url.searchParams.set(paramName, encoded);

    // Use replaceState to update URL without navigation
    window.history.replaceState({}, "", url.toString());
  }, [currentStateName, state.context, childActorId, childSnapshot]);

  // Loading states
  if (isLoading || isHydrating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" />
          <p className="text-gray-600">Loading kiosk...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-red-600">
          <h1 className="text-2xl font-bold mb-2">Error</h1>
          <p>{error.message}</p>
        </div>
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-gray-600">
          <p>No kiosk flow found</p>
        </div>
      </div>
    );
  }

  // Handler to restart the flow
  const handleRestart = async () => {
    // Wrap async cleanup in try-catch so the reset always completes.
    // If setUserLocale (a server action) fails, the unhandled rejection
    // would silently abort the function and leave the user stuck.
    try {
      await setUserLocale("en" as LanguageCode);
    } catch (error) {
      datadogLogs.logger.warn("[kiosk-renderer] Failed to reset locale", {
        error,
      });
    }

    try {
      await clearKioskPatientSession();
    } catch (error) {
      datadogLogs.logger.warn(
        "[kiosk-renderer] Failed to clear patient session",
        { error },
      );
    }

    // Disable state persistence during restart
    shouldPersistState.current = false;

    // Cancel any pending POS payment to avoid leaving terminal in limbo
    if (stripeReaderId) {
      try {
        await cancelPOSPaymentByReaderId(stripeReaderId);
        console.log("[kiosk-renderer] Cancelled POS payment on restart");
      } catch (error) {
        console.warn("[kiosk-renderer] Failed to cancel POS payment:", error);
      }
    }

    // Reset Datadog session to prevent exorbitantly long replays
    // Note: resetDatadogSession has its own internal try-catch
    resetDatadogSession();

    if (NAVIGATION_MODE === "window") {
      // Old behavior: page reload (re-requests camera permission)
      // Set flag to prevent state persistence after reload
      sessionStorage.setItem("kiosk_restarting", "true");

      // Clear all stored states
      clearAllStates();

      // Use replace to avoid race condition with useEffect persisting state
      window.location.replace(window.location.pathname);
    } else {
      // New behavior: remount provider with fresh machine (preserves camera permission)
      // Clear URL parameters to prevent re-hydration from old state
      const url = new URL(window.location.href);
      url.searchParams.delete("stateId");
      url.searchParams.delete("state");
      window.history.replaceState({}, "", url.toString());

      // Clear stored states
      clearAllStates();

      // Reset machine by forcing provider remount (like page refresh but without camera loss)
      resetMachine();

      // Re-enable state persistence after restart completes
      // Use setTimeout to ensure it happens after the provider remounts
      setTimeout(() => {
        shouldPersistState.current = true;
      }, 100);
    }
  };

  // Handler for inactivity timeout - checks for version updates first
  const handleInactivityTimeout = async () => {
    // Check for version updates on every AFK timeout
    // This ensures kiosks get updated during idle periods
    try {
      const needsUpdate = await shouldRefreshForUpdate("[kiosk]");
      if (needsUpdate) {
        datadogLogs.logger.info(
          "[kiosk] Version update available, refreshing",
          {
            currentVersion: getBuildVersion(),
          },
        );
        // Force a full page reload to get new chunks
        window.location.reload();
        return;
      }
    } catch (error) {
      datadogLogs.logger.warn("[kiosk] Failed to check for updates", { error });
      // Continue with normal timeout handling if version check fails
    }

    if (NAVIGATION_MODE === "window") {
      // Old behavior: page reload
      if (currentStateName === "welcome") {
        handleJumpToState("standby");
      } else {
        handleRestart();
      }
    } else {
      // New behavior: machine reset (preserves camera permission)
      if (currentStateName === "welcome") {
        send({ type: "GO_TO_STANDBY" });
      } else {
        // Just call handleRestart which now uses resetMachine()
        handleRestart();
      }
    }
  };

  // Handler to jump to a specific state (for DevTool)
  const handleJumpToState = (
    stateName: string,
    arc?: { arcId: string; stateName: string | Record<string, unknown> },
  ) => {
    const url = new URL(window.location.href);
    const currentStateId = url.searchParams.get("stateId");

    const serialized: {
      value: string | object;
      context: Record<string, unknown>;
      arc?: {
        arcId: string;
        stateName: string | Record<string, unknown>;
        context: Record<string, unknown>;
      };
    } = {
      value: stateName,
      context: state.context,
    };

    // Arc jump: include arc field so the provider builds a persisted snapshot
    // with the child arc actor restored to the specified internal state.
    // dotToStateValue converts "ocrLicense.licenseFrontPhoto" → { ocrLicense: "licenseFrontPhoto" }
    if (arc) {
      // Use the current arc context if jumping within the same active arc,
      // so actor-fetched data (e.g., intake form sections) is preserved.
      const currentArc = traceInspector.getChildSnapshot();
      const arcContext =
        currentArc && currentArc.arcId === arc.arcId ? currentArc.context : {};
      serialized.arc = {
        arcId: arc.arcId,
        stateName:
          typeof arc.stateName === "string"
            ? dotToStateValue(arc.stateName)
            : arc.stateName,
        context: arcContext,
      };
    }

    const { encoded, method } = serializeStateHybrid(
      serialized,
      currentStateId || undefined,
    );

    const paramName = method === "storage" ? "stateId" : "state";
    url.searchParams.set(paramName, encoded);
    url.searchParams.delete(method === "storage" ? "state" : "stateId");

    window.location.href = url.toString();
  };

  // Handler to update machine context (for DevTool)
  const handleContextChange = (updates: {
    orgId?: number;
    practiceId?: string;
    orgName?: string;
    qrMode?: boolean;
    stripeReaderId?: string | null;
  }) => {
    // Serialize state with updated context, preserving arc data if in an arc state
    const serialized: {
      value: string | object;
      context: Record<string, unknown>;
      arc?: {
        arcId: string;
        stateName: string | Record<string, unknown>;
        context: Record<string, unknown>;
      };
    } = {
      value: state.value,
      context: {
        ...state.context,
        ...updates,
      },
    };

    // Include arc child state so the safety net doesn't discard on reload
    if (childActorId && childSnapshot) {
      serialized.arc = {
        arcId: childActorId,
        stateName: childSnapshot.value,
        context: childSnapshot.context,
      };
    }

    // Extract current stateId from URL to reuse it
    const url = new URL(window.location.href);
    const currentStateId = url.searchParams.get("stateId");

    // Reuse existing stateId if available
    const { encoded, method } = serializeStateHybrid(
      serialized,
      currentStateId || undefined,
    );

    // Build new URL with appropriate param (stateId or state)
    const paramName = method === "storage" ? "stateId" : "state";
    url.searchParams.set(paramName, encoded);

    // Direct navigation to reload with updated context
    window.location.href = url.toString();
  };

  // Handler to update Stripe Reader ID (for DevTool)
  const handleStripeReaderIdChange = (readerId: string | null | undefined) => {
    // Update context with new reader ID
    handleContextChange({ stripeReaderId: readerId });
  };

  // Get current state meta - handle hierarchical states and arc child actors
  const getCurrentStateNode = () => {
    // ─── Arc child actor: resolve component from child snapshot ───
    if (childSnapshot) {
      const meta = childSnapshot.getMeta?.();
      if (meta) {
        // getMeta() returns { "identity.ocrLicense.ocrLicenseQuestion": { componentName, ... } }
        // Find the deepest entry with a componentName (most specific active state)
        const entries = Object.entries(meta) as [
          string,
          Record<string, unknown>,
        ][];
        const componentEntry = entries
          .filter(([, m]) => m?.componentName)
          .sort((a, b) => b[0].split(".").length - a[0].split(".").length)[0];

        if (componentEntry) {
          return { meta: componentEntry[1] };
        }
      }
      return undefined;
    }

    // ─── Parent-owned states: existing compound state walking ───
    const parentStateNode = machine.states[currentStateName];

    // Check if this state has substates
    if (
      parentStateNode?.states &&
      Object.keys(parentStateNode.states).length > 0
    ) {
      // Get the active child state from state.value
      if (typeof state.value === "object" && state.value !== null) {
        const childStateName = state.value[currentStateName];
        if (childStateName && parentStateNode.states[childStateName]) {
          return parentStateNode.states[childStateName];
        }
      }

      // Fallback to initial state if defined
      if (
        parentStateNode.initial &&
        typeof parentStateNode.initial === "string"
      ) {
        return parentStateNode.states[parentStateNode.initial];
      }
    }

    // Return the parent state if no substates
    return parentStateNode;
  };

  const currentStateNode = getCurrentStateNode();
  const componentName = currentStateNode?.meta?.componentName;
  const contentKey = currentStateNode?.meta?.contentKey;

  // Skip rendering if no component (invoke-only states)
  if (!componentName || !contentKey) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" />
          <p className="text-gray-600">Processing...</p>
        </div>
      </div>
    );
  }

  // Get component and content
  const Component = componentRegistry.get(componentName);
  const content = bundle.content[contentKey];

  if (!Component) {
    console.error(`Component "${componentName}" not found in registry`);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-red-600">
          <h1 className="text-2xl font-bold mb-2">Component Not Found</h1>
          <p>Component "{componentName}" could not be loaded</p>
        </div>
      </div>
    );
  }

  if (!content) {
    console.error(`Content for key "${contentKey}" not found in bundle`, {
      availableKeys: Object.keys(bundle.content),
    });
  }

  // Merge state meta.metadata into context (e.g., sectionName, cardType, side, isPrimary)
  // When in an arc, use the child's context (has accumulating form data)
  const baseContext = childSnapshot ? childSnapshot.context : state.context;
  const mergedContext = {
    ...baseContext,
    ...(currentStateNode?.meta?.metadata || {}),
    arcId: childActorId,
  };

  // Apply context-sensitive content overrides based on runtime machine context.
  // When in an arc, the child context may not have session-level flags like
  // checkingInForSomeoneElse / under18 (set during triage). Fall back to the
  // parent context so overrides work regardless of which arc is active.
  const resolvedContent = {
    ...content,
    ...getContextSensitiveOverrides(content ?? {}, {
      ...state.context,
      ...mergedContext,
    }),
  };

  const fullWindowComponents = new Set([
    "Welcome",
    "Standby",
    "PhotoCapture",
    "StorefrontComponent",
    "MedriteLoader",
    "ScreenshotAnimation",
  ]);

  // Render only the active component (simpler than React.Activity for now)
  return (
    <div className="min-h-dvh min-w-full flex items-center justify-center relative pb-[5vh]">
      {/* Animated background layer */}
      <motion.div
        className="absolute inset-0 -z-10"
        animate={{
          background: `radial-gradient(73.25% 73.25% at ${gradientPosition.x}% ${gradientPosition.y}%, #F2F3F8 42.31%, rgba(242, 243, 248, 0.00) 100%), #E1EFFE`,
        }}
        transition={{
          background: {
            duration: 0.6,
            ease: "easeInOut",
          },
        }}
      />
      <KioskHeader
        machineContext={mergedContext}
        currentStateName={currentStateName}
      />

      {!fullWindowComponents.has(componentName) && (
        <div className="top-6 fixed z-[10] pointer-events-auto -translate-x-1/2 left-1/2 md:hidden">
          <img
            src={"https://rsup2.b-cdn.net/medrite/Medrite_LogoBlue.png"}
            alt="Logo"
            className="h-6"
          />
        </div>
      )}

      {/* Restart dialog */}
      <RestartDialog
        onRestart={handleRestart}
        onOpenSettings={() => setDevToolOpen(true)}
        qrMode={state.context.qrMode === true}
      />

      {/* Inactivity timeout - no dialog on welcome/standby, just redirect */}
      <InactivityTimeout
        timeoutDuration={inactivityTimeout}
        onTimeout={handleInactivityTimeout}
        showDialog={!["welcome", "standby"].includes(currentStateName)}
        doNotTimeOut={
          "standby" === currentStateName || componentName === "LoadingSpinner"
        }
      />

      {/* Dev-only: DevTool with inspect, jump, and tree views */}
      <KioskDevTool
        open={devToolOpen}
        onOpenChange={setDevToolOpen}
        state={state}
        flow={mockFlow}
        machine={machine}
        onStateChange={handleJumpToState}
        onContextChange={handleContextChange}
        inactivityTimeout={inactivityTimeout}
        onInactivityTimeoutChange={setInactivityTimeout}
        stripeReaderId={stripeReaderId}
        onStripeReaderIdChange={handleStripeReaderIdChange}
        configHealthCheck={configHealthCheck}
        onRerunHealthCheck={rerunHealthCheck}
      />

      {/* Persistent loader overlay - only visible during LoadingSpinner states */}
      <AnimatePresence mode="wait">
        {componentName === "LoadingSpinner" && (
          <motion.div
            key={componentName}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ marginTop: "-300px" }}
            initial={{
              opacity: 0,
              transition: { duration: 0.4, delay: 0.45, ease: "easeInOut" },
            }}
            animate={{
              opacity: 1,
              transition: { duration: 0.4, delay: 1, ease: "easeInOut" },
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <MedriteLoader />
          </motion.div>
        )}
      </AnimatePresence>

      <div
        key={stateKey}
        className={cn(
          componentName !== "PhotoCapture" && "kiosk-component-enter",
          "min-w-full flex items-start justify-center",
          !fullWindowComponents.has(componentName) && "px-6 md:px-12",
        )}
      >
        <ComponentErrorBoundary
          componentName={componentName}
          onRestart={handleRestart}
        >
          <Component
            content={resolvedContent || {}}
            context={mergedContext}
            onEvent={(event: string, data?: unknown) => {
              // Flush captured DOM events and attach to trace BEFORE machine event fires
              const domEvents = domEventSpy.flush();
              if (domEvents.length > 0) {
                traceInspector.addUIHint({
                  component: componentName,
                  action: "click",
                  formData: data as Record<string, unknown> | undefined,
                  domEvents,
                });
              }

              // Intercept FINISH and RESTART events to trigger full machine reset
              if (event === "FINISH" || event === "RESTART") {
                handleRestart();
              } else if (childActorRef) {
                // Inside an arc: send directly to child actor
                childActorRef.send({ type: event, data });
              } else {
                send({ type: event, data });
              }
            }}
          />
        </ComponentErrorBoundary>
      </div>
    </div>
  );
}
